import {
  CROUCH_EYE, CROUCH_HEIGHT, FATIGUE_MAX, RESISTABLE, SPREAD_STILL,
  STAND_EYE, STAND_HEIGHT,
  type ClassId, type Element, type Faction,
} from './constants.ts';
import { CLASSES, hpAt, ppAt } from './classes.ts';
import { levelForXp, tierForSlot, xpForLevel } from './progression.ts';
import { SLOT_ORDER, SPELLS_BY_ID, defaultLoadout, type SlotRole } from './spells.ts';
import { v3, type Vec3 } from './math.ts';
import type { Dice } from './dice.ts';

export interface CastingState {
  spellId: string;
  /** The slot it was begun from. Readying a different one abandons it. */
  slot: number;
  /** Seconds left. */
  remaining: number;
  /** What it started at, so the bar knows how full to draw itself. */
  total: number;
}

export interface ActiveEffect {
  spellId: string;
  /**
   * A protective buff, as opposed to anything else that happens to carry a
   * `resist` figure. Fracture is a negative resistance and must not be mistaken
   * for one of these — it is the thing they defend against.
   */
  protection?: boolean;
  /** What `remaining` started at, so the HUD can draw how much is left. */
  total?: number;
  /** Element this effect resists, for buff kinds. */
  element?: Element;
  /** 0..1 fractional damage reduction, for buff kinds. */
  resist?: number;
  /** Damage per second, for debuff kinds. */
  dps?: number;
  /** Movement multiplier while active. 0 is a root. */
  slow?: number;
  /**
   * Marks the one effect cold uses, so a second ice hit finds it.
   *
   * By flag rather than by spell id, because three different spells all apply
   * the same chill and a mage hit by a dart and then a ball should carry one
   * slow, not two that stack into a root.
   */
  chilled?: boolean;
  /**
   * Feet nailed to the floor: no jumping, no flight.
   *
   * Separate from `slow: 0` rather than implied by it, because the two govern
   * different axes. `slow` scales the horizontal step, and a jump is vertical —
   * so a mage under a `slow: 0` effect and nothing else could still leap, which
   * is not what "rooted in place" describes. A wall that slows to a standstill
   * is not a root and should not stop a jump; Paralysis is and does.
   */
  rooted?: boolean;
  /**
   * Discrete damage ticks — `tickDamage` every `tickEvery` seconds. Burning uses
   * this rather than `dps` because its whole character is a countable number of
   * one-point ticks that a second hit lengthens; a fractional rate would round
   * to nothing at 60Hz and could not be described to the player as "six ticks".
   */
  tickEvery?: number;
  tickDamage?: number;
  /** Rolled fresh every tick, where a fixed `tickDamage` would be too flat. */
  tickDice?: Dice;
  nextTick?: number;
  remaining: number;
  sourceId?: string;
}

export interface PlayerState {
  /**
   * Whether this mage has said they are ready to start.
   *
   * Only meaningful while the room is in its lobby, and ignored everywhere else.
   * On the player rather than in a side table on the room because it has to
   * reach every client in the ordinary snapshot: the whole point of a shared
   * lobby is that everybody sees the same ticks going up.
   */
  ready: boolean;
  id: string;
  name: string;
  faction: Faction;
  cls: ClassId;

  pos: Vec3;
  /** Facing, radians. yaw drives movement; pitch is look only. */
  yaw: number;
  pitch: number;
  /**
   * Where this mage was at the start of the current tick.
   *
   * Purely for drawing. The simulation never reads it — it exists so the
   * renderer can put a mage between two simulated positions rather than at the
   * later of them, which is the difference between movement that steps at sixty
   * hertz and movement that is smooth at whatever the monitor runs at.
   */
  prevPos?: Vec3;

  /**
   * Seconds left of being allowed to have moved impossibly far.
   *
   * Set when the game itself relocates a mage — a Cleric's call, a resurrection,
   * the way out of Valhalla — so the movement guard can tell those apart from a
   * client claiming to have crossed the nave in a tick. See `inputGuard.ts`.
   */
  warpedFor?: number;

  /**
   * External horizontal velocity, from impulses only — WASD movement is not
   * integrated through this. Decays back to zero on its own.
   */
  vel: Vec3;
  /**
   * Written by the controller, which is still the only thing that integrates it,
   * but held here rather than privately so the server can reproduce the same arc.
   * Jumping tolerated a renderer-private value; Sign of Expulsion does not,
   * because a launch that clears the Crypt gallery on one machine and not the
   * other is a desync you can see.
   */
  verticalVelocity: number;
  onGround: boolean;
  flying: boolean;

  /**
   * Movement multiplier from placed effects, 1 when unimpeded. Core recomputes
   * it from scratch every tick from barrier overlap, so it cannot get stuck at a
   * stale value when a wall expires underneath someone.
   */
  moveScale: number;
  /** Set by core, consumed and cleared by the controller. Sign of Expulsion. */
  pendingImpulse: Vec3 | null;

  /**
   * A spell winding up, or null.
   *
   * Nothing outside the caster can stop this: no interrupt on damage, no
   * pushback, no root breaking it. The cost of a cast time is paid in movement —
   * half speed for the duration — and the caster alone can abandon it, by
   * readying something else.
   */
  casting: CastingState | null;
  /** Seconds until Resurrect is available again. */
  raiseCooldown: number;
  /** Seconds until Call Ghost is available again. Separate, so one is not the other. */
  callCooldown: number;


  /**
   * Time left on the global cooldown. Every cast locks every slot, so cycling
   * 1-2-3 no longer fires three spells in the time one was meant to take.
   */
  gcd: number;
  /**
   * Whether this mage is actually running, as opposed to holding the key. Core
   * decides it once and everything else reads it — the renderer for speed, the
   * spread for the reticle — so the two can never disagree about it.
   */
  running: boolean;
  /** Ran the tank dry, and has not yet recovered enough to run again. */
  exhausted: boolean;
  /** Flew the power bar empty, and has not recovered enough to take off again. */
  drained: boolean;
  /**
   * Held down, not toggled, and only ever true on the ground. The capsule, the
   * eye and the hitbox all shrink with it.
   */
  crouching: boolean;
  /**
   * Cone half-angle a projectile will be scattered within, radians. Recomputed
   * every tick from what the player is doing, and drawn as the reticle — so what
   * the reticle shows is what the shot actually does.
   */
  spread: number;

  /**
   * Which element each slot's spell is tuned to, for the protection lines that
   * offer a choice. Indexed by slot, so it survives switching between slots and
   * cannot be confused between two tunable spells.
   */
  slotElement: number[];
  /**
   * Tab presses already applied. The input carries a monotonic count rather than
   * a flag because the simulation ticks on its own clock: a boolean set and
   * cleared between two ticks is a press that never happened, which is the bug
   * that made spell slots need several presses back in M1.
   */
  elementCyclesSeen: number;

  level: number;
  xp: number;
  /** Experience banked this match, before the Valhalla penalty applies. */
  matchXp: number;

  hp: number;
  hpMax: number;
  pp: number;
  ppMax: number;
  fatigue: number;

  /** 0..1. The purple bar: how fast power comes back, not how much you have. */
  ley: number;
  /** Seconds since the Arcanist last stood in any pool. */
  poolChargeAge: number;

  alive: boolean;
  ghost: boolean;
  inValhalla: boolean;

  /** Six spell ids in SLOT_ORDER. */
  loadout: string[];
  selectedSlot: number;
  /** Remaining cooldown per slot, seconds. */
  cooldowns: number[];

  effects: ActiveEffect[];

  kills: number;
  deaths: number;
  poolsBiased: number;
  shrinesConquered: number;
  damageDealt: number;

  /** Fractional power carried between ticks. Truncating each tick regenerates nothing. */
  ppAccumulator: number;
  hpAccumulator: number;

  /**
   * Test combatants set this so they stay at their configured level. A dummy
   * that fires continuously earns experience continuously, and an unattended one
   * will out-level a live player inside a couple of minutes.
   */
  frozenLevel?: boolean;
}

/**
 * `level` exists because slots unlock at different levels now, so a great many
 * callers — tests especially — need a mage who can actually cast the thing they
 * are about to cast. Setting `.level` afterwards is not the same: hit points and
 * power are read from the table at creation and would stay at their level-one
 * values, leaving a mage who has the tier but cannot afford it.
 */
export function makePlayer(
  id: string, name: string, faction: Faction, cls: ClassId, pos: Vec3 = v3(),
  level = 1,
): PlayerState {
  const hpMax = hpAt(cls, level);
  const ppMax = ppAt(cls, level);
  return {
    id, name, faction, cls, ready: false,
    pos: { ...pos }, yaw: 0, pitch: 0, vel: v3(), verticalVelocity: 0,
    onGround: true, flying: false,
    moveScale: 1, pendingImpulse: null, casting: null, raiseCooldown: 0, callCooldown: 0,
    gcd: 0, running: false, exhausted: false, drained: false,
    crouching: false, spread: SPREAD_STILL,
    slotElement: SLOT_ORDER.map(() => 0),
    elementCyclesSeen: 0,
    level, xp: xpForLevel(level), matchXp: 0,
    hp: hpMax, hpMax, pp: ppMax, ppMax, fatigue: FATIGUE_MAX,
    ley: 0, poolChargeAge: Infinity,
    alive: true, ghost: false, inValhalla: false,
    loadout: defaultLoadout(cls), selectedSlot: 0, cooldowns: [0, 0, 0, 0, 0, 0],
    effects: [],
    kills: 0, deaths: 0, poolsBiased: 0, shrinesConquered: 0, damageDealt: 0,
    ppAccumulator: 0, hpAccumulator: 0,
  };
}

/** Applies experience and levels up, scaling max hit points and power. */
export function awardXp(p: PlayerState, amount: number): boolean {
  if (p.frozenLevel) return false;
  p.xp += amount;
  p.matchXp += amount;
  const next = levelForXp(p.xp);
  if (next === p.level) return false;

  const hpBefore = p.hpMax;
  const ppBefore = p.ppMax;
  p.level = next;
  p.hpMax = hpAt(p.cls, next);
  p.ppMax = ppAt(p.cls, next);
  // Levelling grants the new headroom immediately rather than healing you up.
  p.hp += p.hpMax - hpBefore;
  p.pp += p.ppMax - ppBefore;
  return true;
}

/**
 * In the world, and able to be acted on by it — damaged, healed, counted at a
 * pool, targeted by a sign.
 *
 * Valhalla is the reason this is a function rather than two inline conditions.
 * A player serving time there is alive and solid, but they are forty metres
 * below the arena, and `dist2d` does not care about height: the corridor runs
 * directly under the nave pool, the north pool and the Order shrine, so without
 * this they would hold three objectives from underground while untouchable.
 */
/**
 * Whether this mage's feet are pinned — no jump, no flight.
 *
 * One function so the simulation and the renderer's character controller cannot
 * form separate opinions. They already had to be reminded once that speed is
 * core's answer and not a second opinion; a root is the same kind of fact.
 */
export function isRooted(p: PlayerState): boolean {
  return p.effects.some((e) => e.rooted);
}

export function isEmbodied(p: PlayerState): boolean {
  return p.alive && !p.ghost && !p.inValhalla;
}

/** Standing height, or crouched height while they are down. */
export function playerHeight(p: PlayerState): number {
  return p.crouching ? CROUCH_HEIGHT : STAND_HEIGHT;
}

/** Where a spell leaves this mage, and where they see from. */
export function eyeHeight(p: PlayerState): number {
  return p.crouching ? CROUCH_EYE : STAND_EYE;
}

/**
 * Whether this mage counts towards holding a pool or a shrine.
 *
 * Stricter than being in the world: you have to have your feet on the floor.
 * A Mentalist hovering above a pool was drawing it their way without ever
 * being reachable by anyone standing in it, which made flight the answer to an
 * objective it was never meant to be the answer to.
 *
 * Note this is about *presence*, not about being a target. A flier is still
 * perfectly shootable — they simply do not count for the objective underneath.
 */
export function holdsObjectives(p: PlayerState): boolean {
  return isEmbodied(p) && !p.flying && p.onGround;
}

/**
 * Tier of the spell in a given slot, or 0 if that slot has not unlocked yet.
 *
 * Slot-aware because the schedule is: a level-4 mage has their Primary at tier I
 * and their Area at nothing at all. There is no single "current tier" for a
 * player any more, and anything asking for one is asking the wrong question.
 */
export function tierInSlot(p: PlayerState, slot: SlotRole): number {
  return tierForSlot(slot, p.level);
}

/** Tier of whatever is readied. Zero means it cannot be cast at all yet. */
export function currentTier(p: PlayerState): number {
  const spell = SPELLS_BY_ID[p.loadout[p.selectedSlot] ?? ''];
  if (!spell) return 0;
  return tierForSlot(spell.slot, p.level);
}

/** Total resistance against an element, 0..1. Only four schools can be resisted. */
/**
 * Drops any protection already granted to this player by this caster.
 *
 * One protection per source, self included: a mage who re-guards themselves
 * replaces what they had, and a Cleric guarding a teammate replaces only their
 * own earlier work. Layered cover therefore costs a second caster's attention,
 * which is the point — it is a thing a team does, not a thing a mage does alone
 * by cycling through the schools.
 */
export function clearProtectionFrom(p: PlayerState, sourceId: string): void {
  for (let i = p.effects.length - 1; i >= 0; i--) {
    const e = p.effects[i];
    if (e.protection && e.sourceId === sourceId) p.effects.splice(i, 1);
  }
}

export function resistanceTo(p: PlayerState, element: Element): number {
  let total = 0;
  for (const e of p.effects) {
    if (e.resist === undefined) continue;

    // Negative resistance is vulnerability, and it applies to everything. Only
    // four schools can be *resisted*, but a fractured bone hurts whatever hits
    // it — gating vulnerability behind RESISTABLE would make Fracture silently
    // do nothing against Wounding, Void or Mana, which is most of what it would
    // be set up to enable.
    if (e.resist < 0) {
      total += e.resist;
      continue;
    }

    if (!RESISTABLE.has(element)) continue;
    // A Prayer with no element set covers everything, at a lower rate.
    if (e.element === undefined || e.element === element) total += e.resist;
  }
  // Floored as well as capped, so stacked Fractures cannot turn a dart lethal.
  return Math.max(-0.75, Math.min(0.8, total));
}

export interface DamageResult {
  applied: number;
  killed: boolean;
}

export function applyDamage(
  p: PlayerState, amount: number, element: Element,
): DamageResult {
  if (!isEmbodied(p)) return { applied: 0, killed: false };
  const applied = Math.max(0, Math.round(amount * (1 - resistanceTo(p, element))));
  p.hp = Math.max(0, p.hp - applied);
  return { applied, killed: p.hp === 0 };
}

export function heal(p: PlayerState, amount: number): number {
  if (!isEmbodied(p)) return 0;
  const before = p.hp;
  p.hp = Math.min(p.hpMax, p.hp + amount);
  return p.hp - before;
}

export function spendPp(p: PlayerState, amount: number): boolean {
  if (p.pp < amount) return false;
  p.pp -= amount;
  return true;
}

export function drainPp(p: PlayerState, amount: number): number {
  const before = p.pp;
  p.pp = Math.max(0, p.pp - amount);
  return before - p.pp;
}

export function canBiasPools(p: PlayerState): boolean {
  return CLASSES[p.cls].canBiasPools;
}

export function canConquerShrines(p: PlayerState): boolean {
  return CLASSES[p.cls].canConquerShrines;
}
